import React, { useState, useEffect } from 'react';
import {
    EditButton,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    ReferenceInput,
    useNotify,
    useRefresh,
    Confirm,
    Button
} from 'react-admin';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';

const DeleteCourseButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/courses/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Course deleted successfully', { type: 'success' });
            refresh();
            // Force reload of the list if refresh doesn't trigger a re-mount/re-fetch in this custom setup
            window.location.reload();
        } catch (error) {
            console.error('Error deleting course:', error);
            notify('Failed to delete course', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                label="Delete"
                color="error"
                startIcon={<DeleteIcon />}
            />
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Course"
                content="Are you sure you want to delete this course? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

export const CourseList = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/courses?end=1000`; // Fetch all for client-side sort
        httpClient(url)
            .then(({ json }) => {
                setCourses(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching courses:', error);
                setLoading(false);
            });
    }, []);

    const columns = [
        { key: 'category_name', header: 'Category' },
        { key: 'title', header: 'Title' },
        { key: 'complexity', header: 'Complexity' },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <>
                    <EditButton record={record} basePath="/courses" />
                    <DeleteCourseButton record={record} />
                </>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Courses</h2>
                <Link
                    to="/courses/create"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Create
                </Link>
            </div>
            <SortableTable data={courses} columns={columns} />
        </div>
    );
};

const CourseForm = ({ isCreate = false }) => {
    const [complexityChoices, setComplexityChoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/course_complexities/`;
        httpClient(url)
            .then(({ json }) => {
                const choices = (json || []).map((c) => ({ id: c.id, name: c.name }));
                setComplexityChoices(choices);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching complexities:', error);
                setLoading(false);
            });
    }, []);

    if (loading) return <p>Loading...</p>;

    return (
        <SimpleForm>
            <ReferenceInput source="category_id" reference="course_categories">
                <SelectInput optionText="name" />
            </ReferenceInput>
            <TextInput source="title" />
            <SelectInput source="complexity_id" label="Complexity" optionValue="id" optionText="name" choices={complexityChoices} />
            <TextInput source="description" multiline fullWidth />
        </SimpleForm>
    );
};

export const CourseEdit = () => (
    <Edit title="Edit Course">
        <>
            <h2>Courses</h2>
            <CourseForm />
        </>
    </Edit>
);

export const CourseCreate = () => (
    <Create title="Create Course">
        <>
            <h2>Courses</h2>
            <CourseForm isCreate={true} />
        </>
    </Create>
);
